import React, { FC } from "react";
import { observer } from "mobx-react";
import { toJS } from "mobx";
import DeleteIcon from "../../../../../../images/common/trash-red.svg";
import styled from "styled-components";
import {
  DescriptionCell,
  LabelCell,
  MediaBoxesWrapper,
  SelectedColorButton,
} from "../../../shared/styles";
import {
  CustomLabel,
  FlexContainer,
  Input,
  ColorPicker as ColorPickerComponent,
} from "@mindmemobile/mm-shared-global";
import {
  CheckoutBrandingModel,
  ColorPickerElement,
  DonationCheckoutModel,
  MediaModel,
} from "../../../shared/models";
import {
  ImageFormatTypes,
  ImagePlacementTypes,
  MediaTypes,
} from "../../../shared/enums";
import { MediaBoxes } from "../../../shared/components";
import { MediaItem } from "@mindmemobile/mm-shared-library";

export type ColorPickerType = "main" | "border" | "background" | "progress";
type IProps = {
  donationOptions: DonationCheckoutModel;
  onChange: (
    field: keyof CheckoutBrandingModel,
    value: DonationCheckoutModel,
  ) => void;
  toggleLibrary: (show: boolean, fromUpload?: boolean) => void;
  showLibraryView: boolean;
};

export const OptionCheckout = styled(FlexContainer)`
  ${MediaBoxesWrapper} {
    .label-description-row {
      margin: 0px !important;
    }
  }
  .checkout-media-row {
    margin-bottom: 30px;
  }
  .checkout-media-checkbox {
    margin-left: 40px;
  }
  .chkout-radio-btn {
    input.form-check-input {
      height: 18px;
      width: 18px;
      margin-top: 3px !important;
      background-size: 26px !important;
    }
    label {
      font-size: 16px;
      color: #555;
    }
  }
`;

const CheckoutDonationOptions: FC<IProps> = ({
  onChange,
  donationOptions,
  toggleLibrary,
  showLibraryView,
}) => {
  const handleColorPickerChange = (
    keyy: keyof DonationCheckoutModel,
    index: number,
    key: keyof ColorPickerElement,
    value: any,
  ) => {
    const colorPickers = donationOptions[keyy] as ColorPickerElement[];
    colorPickers[index] = new ColorPickerElement({
      ...colorPickers[index],
      [key]: value,
    });
    onChange(
      "donationCheckout",
      new DonationCheckoutModel({ ...donationOptions, [keyy]: colorPickers }),
    );
  };

  const handleSliderChange = (value: number) => {
    onChange(
      "donationCheckout",
      new DonationCheckoutModel({ ...donationOptions, placementSlider: value }),
    );
  };

  const updateMedia = (medias: MediaModel[]) => {
    onChange(
      "donationCheckout",
      new DonationCheckoutModel({ ...donationOptions, media: medias }),
    );
  };

  const onDelete = (media: MediaModel) => {
    const updatedMedia = donationOptions.media.filter(
      (m) => m.mediaId !== media.mediaId,
    );
    onChange(
      "donationCheckout",
      new DonationCheckoutModel({ ...donationOptions, media: updatedMedia }),
    );
  };

  const onSelectLibraryMedia = (media: MediaItem) => {
    const updatedMedia = [
      ...donationOptions.media,
      MediaModel.mediaItemToMediaModel(media),
    ];
    onChange(
      "donationCheckout",
      new DonationCheckoutModel({ ...donationOptions, media: updatedMedia }),
    );
  };

  const onFavourite = (media: MediaModel) => {
    const updatedMedia = donationOptions.media.map((m) => {
      if (m.mediaId === media.mediaId) {
        m.isFavourite = !m.isFavourite;
      }
      return m;
    });
    onChange(
      "donationCheckout",
      new DonationCheckoutModel({ ...donationOptions, media: updatedMedia }),
    );
  };

  const renderColorPickerElements = (
    colorPickers: ColorPickerElement[],
    label: string,
    key: keyof DonationCheckoutModel,
  ) => {
    return (
      <FlexContainer className="label-description-row">
        <LabelCell>
          <CustomLabel className="standard-lh">{label}</CustomLabel>
        </LabelCell>
        <DescriptionCell>
          <FlexContainer
            fxWrap="wrap"
            className="position-relative"
            spacing={9}
          >
            {colorPickers.map((picker, index) => (
              <div key={index} className="colored-block-div">
                <FlexContainer
                  spacing={10}
                  alignItems="center"
                  className="position-relative"
                >
                  <SelectedColorButton
                    heightWidth="24px"
                    padding="3px"
                    borderRadius="2px"
                    onClick={() =>
                      handleColorPickerChange(
                        key,
                        index,
                        "showColorPicker",
                        !picker.showColorPicker,
                      )
                    }
                    color={picker.color?.rgba}
                    className="color-picker-gallery"
                    // className="opaque-unselected" // creating issue for options page color picker
                  >
                    <div />
                  </SelectedColorButton>
                  {picker.showColorPicker && (
                    <ColorPickerComponent
                      color={picker.deserializeColor()}
                      onChange={(color) =>
                        handleColorPickerChange(key, index, "color", color)
                      }
                      onHide={() =>
                        handleColorPickerChange(
                          key,
                          index,
                          "showColorPicker",
                          false,
                        )
                      }
                      className="checkout-color-picker"
                    />
                  )}
                  <CustomLabel>{picker.name}</CustomLabel>
                </FlexContainer>
              </div>
            ))}
          </FlexContainer>
        </DescriptionCell>
      </FlexContainer>
    );
  };

  const handleInputChange = (
    key: keyof DonationCheckoutModel,
    value: string | boolean | keyof typeof ImagePlacementTypes,
  ) => {
    onChange(
      "donationCheckout",
      new DonationCheckoutModel({ ...donationOptions, [key]: value }),
    );
  };

  const radioButton = (
    value: boolean,
    fieldName: keyof DonationCheckoutModel,
    label: string,
    updateValue:
      | keyof typeof ImagePlacementTypes
      | keyof typeof ImageFormatTypes,
    className?: string,
  ) => {
    return (
      <Input
        value={value}
        id={`rdb_${label}`}
        type="radio"
        label={label}
        onChange={() => handleInputChange(fieldName, updateValue)}
        className={`chkbox-no-margin ${className}`}
      />
    );
  };

  const checkBoxInput = (
    key: keyof DonationCheckoutModel,
    value: boolean,
    label: string,
    disabled: boolean,
    className?: string,
  ) => {
    return (
      <Input
        type="checkbox"
        value={value}
        onChange={(e) => handleInputChange(key, e.target.checked)}
        label={label}
        disabled={disabled || false}
        className={className}
      />
    );
  };
  return (
    <OptionCheckout fxLayout="column" spacing={30} className="option-checkout">
      <FlexContainer fxLayout="column">
        <div>
          {renderColorPickerElements(
            donationOptions.mainColors,
            "Main",
            "mainColors",
          )}
        </div>
        <div>
          {renderColorPickerElements(
            donationOptions.backgroundColors,
            "Background",
            "backgroundColors",
          )}
        </div>
        <div>
          {renderColorPickerElements(
            donationOptions.borderColors,
            "Border",
            "borderColors",
          )}
        </div>
        <div>
          {renderColorPickerElements(
            donationOptions.fontColors,
            "Font",
            "fontColors",
          )}
        </div>
        <FlexContainer fxLayout="column" spacing={20}>
          <CustomLabel className="checkout-headers">Image</CustomLabel>
          <FlexContainer
            alignItems="center"
            spacing={15}
            className="checkout-media-row"
          >
            <MediaBoxes
              key={"medias"}
              multiple={false}
              medias={toJS(donationOptions.media) || []}
              onUpload={(medias: MediaModel[]) => updateMedia(medias)}
              onDelete={(media: MediaModel) => onDelete(media)}
              onSelectLibraryMedia={(media) => onSelectLibraryMedia(media)}
              onFavourite={(media: MediaModel) => onFavourite(media)}
              showUploadButtons={false}
              hideOverlays={true}
              allowOnlyOne={true}
              hideLabel={true}
              allowedMediaTypes={[MediaTypes.Image]}
              toggleLibrary={(show: boolean, fromUpload?: boolean) =>
                toggleLibrary(show, fromUpload)
              }
              showLibrary={showLibraryView}
            />
            {donationOptions.media.length > 0 && (
              <img
                src={DeleteIcon}
                alt="delete"
                onClick={() => onDelete(donationOptions.media[0])}
                style={{ cursor: "pointer" }}
              />
            )}
          </FlexContainer>
        </FlexContainer>
        {donationOptions.media.length > 0 && (
          <FlexContainer fxLayout="column" spacing={20}>
            <CustomLabel>Image Placement</CustomLabel>
            <FlexContainer spacing={40}>
              {radioButton(
                donationOptions.imagePlacement === ImagePlacementTypes.Left,
                "imagePlacement",
                "Left",
                ImagePlacementTypes.Left,
                "chkout-radio-btn",
              )}
              {radioButton(
                donationOptions.imagePlacement === ImagePlacementTypes.Right,
                "imagePlacement",
                "Right",
                ImagePlacementTypes.Right,
                "chkout-radio-btn",
              )}
              {radioButton(
                donationOptions.imagePlacement ===
                  ImagePlacementTypes.Background,
                "imagePlacement",
                "Background",
                ImagePlacementTypes.Background,
                "chkout-radio-btn",
              )}
              {checkBoxInput(
                "imageHideOnMobile",
                donationOptions.imageHideOnMobile,
                "Hide on Mobile",
                false,
                "checkout-media-checkbox",
              )}
            </FlexContainer>
            {donationOptions.imagePlacement !==
              ImagePlacementTypes.Background && (
              <FlexContainer fxLayout="column" spacing={20}>
                <CustomLabel>Image Format</CustomLabel>
                <FlexContainer spacing={40}>
                  {radioButton(
                    donationOptions.imageFormat === "ScreenFit",
                    "imageFormat",
                    "Fit Screen",
                    ImageFormatTypes.ScreenFit,
                    "chkout-radio-btn",
                  )}
                  {radioButton(
                    donationOptions.imageFormat === "Original",
                    "imageFormat",
                    "Original",
                    ImageFormatTypes.Original,
                    "chkout-radio-btn",
                  )}
                </FlexContainer>
              </FlexContainer>
            )}
            {donationOptions.imagePlacement ===
              ImagePlacementTypes.Background && (
              <FlexContainer fxLayout="column" spacing={20} padding="10px 0px">
                <CustomLabel margin="0px 0px 5px">
                  Donation Placement
                </CustomLabel>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={donationOptions.placementSlider}
                  title={`${donationOptions.placementSlider}%`}
                  onChange={(e) => handleSliderChange(e.target.valueAsNumber)}
                  style={{
                    width: "200px",
                    borderRadius: "10px",
                    height: "6px",
                    cursor: "pointer",
                    accentColor: "green",
                    outline: "none",
                  }}
                />
              </FlexContainer>
            )}
          </FlexContainer>
        )}
      </FlexContainer>
    </OptionCheckout>
  );
};

export default observer(CheckoutDonationOptions);
